import { Component, OnInit } from '@angular/core';
import { Cahier } from '../../../../data/habilitation/models/cahier.model';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { CahiersService } from '../../../../data/commission/services/cahiers/cahiers.service';
import { SessionContext } from '../../../../core/services/config/app.settings';
import { forkJoin } from 'rxjs';
import { TourneesService } from '../../../../data/tournee/services/tournees.service';
import { Tournee } from '../../../../data/tournee/models/tournee.model';
import { CommissionsService } from '../../../../data/commission/services/commissions/commissions.service';
import { EchantillonsService } from '../../../../data/commission/services/echantillons/echantillons.service';
import * as moment from 'moment';
import { EchantillonExport } from '../../../../data/commission/models/echantillon-export.model';
import { ExportToCsv } from 'export-to-csv';
import { map, switchMap } from 'rxjs/operators';
import { OperateursService } from '../../../../data/intervenant/services/operateurs/operateurs.service';
import { Commission } from '../../../../data/commission/models/commission.model';
import { Router } from '@angular/router';
import { Operateur } from '../../../../data/intervenant/models/operateur.model';
import { LoaderService } from '../../../../core/services/loader/loader.service';
import { CommissionFilter } from '../../../../data/commission/models/commission-filter.model';
import { TourneeFilter } from '../../../../data/tournee/models/tournee-filter.model';

@Component({
    selector: 'app-generation-echantillons',
    templateUrl: './generation-echantillons.component.html',
    styleUrls: ['./generation-echantillons.component.scss'],
    standalone: false
})
export class GenerationEchantillonsComponent implements OnInit {
    /**
     * Organisme id connecté
     */
    idOrganisme: number;

    /**
     * Liste des tournées
     */
    tournees: TourneeFilter[] = [];

    /**
     * Liste des commissions
     */
    commissions: CommissionFilter[] = [];

    /**
     * Liste des cahiers des charges
     */
    cahiers: Cahier[] = [];

    /**
     * Liste des échantillons à exporter
     */
    echantillons: EchantillonExport[] = [];

    /**
     * Form de filtrage
     */
    formFilter: UntypedFormGroup;

    /**
     * True s'il y a un résultat
     */
    hasResult = false;

    /**
     * Getter annee
     */
    get annee() {
        return this.formFilter.get('anneeControle');
    }

    /**
     * Getter tournee
     */
    get tournee() {
        return this.formFilter.get('tournee');
    }

    /**
     * Getter commission
     */
    get commission() {
        return this.formFilter.get('commission');
    }

    /**
     * Getter cahier
     */
    get cahier() {
        return this.formFilter.get('cahier');
    }

    /**
     * Getter millesime
     */
    get millesime() {
        return this.formFilter.get('millesime');
    }

    /**
     * Tournée transmise via url
     */
    initialTourneeId: number;

    /**
     * Commission transmise via url
     */
    initialCommissionId: number;

    constructor(
        private readonly fb: UntypedFormBuilder,
        private readonly loaderService: LoaderService,
        private readonly cahiersService: CahiersService,
        private readonly tourneesService: TourneesService,
        private readonly commissionsService: CommissionsService,
        private readonly echantillonsService: EchantillonsService,
        private readonly operateursService: OperateursService,
        private readonly router: Router
    ) {
        // Récupérer l'ID organisme
        this.idOrganisme = SessionContext.get('idOrganisme');

        // Récupérer la tournée/commission via l'url
        const state = this.router.getCurrentNavigation() && this.router.getCurrentNavigation().extras.state;
        if (state) {
            if (state.tournee) {
                this.initialTourneeId = state.tournee;
            }
            if (state.commission) {
                this.initialCommissionId = state.commission;
            }
        }
    }

    /**
     * On init component
     */
    ngOnInit() {
        // Initialise le formulaire de filtrage
        this.formFilter = this.fb.group({
            anneeControle: undefined,
            tournee: undefined,
            commission: undefined,
            cahier: undefined,
            millesime: undefined
        });

        // Charger les donnees pour les filtres
        forkJoin([
            this.tourneesService.getTourneesFilter(this.idOrganisme),
            this.commissionsService.getCommissionsFilter(this.idOrganisme),
            this.cahiersService.getCahiers()
        ])
            .pipe(this.loaderService.operator())
            .subscribe(([tournees, commissions, cahiers]) => {
                this.tournees = tournees.sort((a, b) => moment(b.dateDebut).valueOf() - moment(a.dateDebut).valueOf());
                this.commissions = commissions.sort((a, b) => moment(b.dateDebut).valueOf() - moment(a.dateDebut).valueOf());
                this.cahiers = cahiers.sort((a, b) => a.libelle.localeCompare(b.libelle));

                if (this.initialTourneeId) {
                    const tournee = this.tournees.find(t => t.id === this.initialTourneeId);
                    this.tournee.setValue(tournee);
                }

                if (this.initialCommissionId) {
                    const commission = this.commissions.find(c => c.id === this.initialCommissionId);
                    this.commission.setValue(commission);
                }
            });
    }

    /**
     * Exportation des echantillons
     */
    onExport() {
        this.hasResult = false;
        this.echantillonsService
            .exportEchantillons(this.idOrganisme, this.annee.value, this.millesime.value, this.cahier.value, this.tournee.value, this.commission.value)
            .pipe(
                switchMap(echantillons => {
                    const idsIntervenant = echantillons.map(e => e.idIntervenant);
                    const idsIntervenantTournee = echantillons.map(e => e.idIntervenantTournee);
                    const ids = idsIntervenant.concat(idsIntervenantTournee);
                    return this.operateursService.getOperateurs(ids).pipe(
                        map(operateurs => {
                            const mapOperateurs = new Map();
                            operateurs.forEach(o => {
                                mapOperateurs.set(o.id, o);
                            });
                            return echantillons.map(e => ({
                                ...e,
                                intervenant: mapOperateurs.get(e.idIntervenant),
                                intervenantTournee: mapOperateurs.get(e.idIntervenantTournee)
                            }));
                        })
                    );
                }),
                this.loaderService.operator()
            )
            .subscribe(echantillons => {
                this.hasResult = true;
                this.echantillons = echantillons.sort((a, b) => moment(b.dateEvenement).valueOf() - moment(a.dateEvenement).valueOf());
                if (this.echantillons.length > 0) {
                    this.exporter();
                }
            });
    }

    /**
     * Exporter les jurés
     */
    exporter(): void {
        const data = [];
        this.echantillons.forEach(exp => {
            data.push({
                numeroEchantillon: exp.numeroEchantillon ? exp.numeroEchantillon : '',
                millesimeEchantillon: exp.millesimeEchantillon ? exp.millesimeEchantillon : '',
                raisonSocialeIntervenant: exp.intervenant.raisonSociale ? exp.intervenant.raisonSociale : '',
                cviIntervenant: exp.cviIntervenant ? exp.cviIntervenant : '',
                siretIntervenant: exp.intervenant.siret ? ` ${exp.intervenant.siret} '` : '',
                adresseIntervenant: exp.intervenant.adresse ? exp.intervenant.adresse : '',
                codePostalIntervenant: exp.intervenant.codePostal ? exp.intervenant.codePostal : '',
                villeIntervenant: exp.intervenant.ville ? exp.intervenant.ville : '',
                cahiers: exp.cahiers ? exp.cahiers : '',
                produit: exp.produit ? exp.produit : '',
                couleur: exp.couleur ? exp.couleur : '',
                typeMouvement: exp.typeMouvement ? exp.typeMouvement : '',
                adresseSiteStockage: exp.adresseSiteStockage ? exp.adresseSiteStockage : '',
                cepages: exp.cepages ? exp.cepages : '',
                tav: exp.tav ? exp.tav : '',
                numeroLot: exp.numeroLot ? exp.numeroLot : '',
                logementLot: exp.logementLot ? exp.logementLot : '',
                volumeLot: exp.volumeLot ? exp.volumeLot : '',
                detailsContenants: exp.detailsContenants ? exp.detailsContenants : '',
                dateEvenement: exp.dateEvenement ? moment(exp.dateEvenement).format('YYYY-MM-DD') : '',
                typePrelevement: exp.typePrelevement ? exp.typePrelevement : '',
                numeroExpertise: exp.numeroExpertise ? exp.numeroExpertise : '',
                numeroPassage: exp.numeroPassage ? exp.numeroPassage : '',
                dateButoirControle: exp.dateButoirControle ? moment(exp.dateButoirControle).format('YYYY-MM-DD') : '',
                referenceTournee: exp.referenceTournee ? exp.referenceTournee : '',
                dateTournee: exp.dateTournee ? moment(exp.dateTournee).format('YYYY-MM-DD') : '',
                responsableTournee: this.getResponsableTournee(exp.intervenantTournee),
                referenceCommission: exp.referenceCommission ? exp.referenceCommission : '',
                dateCommission: exp.dateCommission ? moment(exp.dateCommission).format('YYYY-MM-DD') : '',
                numeroJury: exp.numeroJury ? exp.numeroJury : '',
                numeroAnonymat: exp.numeroAnonymat ? exp.numeroAnonymat : '',
                resultatDegustation: exp.resultatDegustation ? exp.resultatDegustation : ''
            });
        });

        const options = {
            filename: 'EXPORT_ECHANTILLONS'.concat('_').concat(moment().format('YYYYMMDD')),
            fieldSeparator: ';',
            quoteStrings: '',
            decimalSeparator: ',',
            showLabels: true,
            showTitle: false,
            title: 'EXPORT_ECHANTILLONS'.concat('_').concat(moment().format('YYYYMMDD')),
            useTextFile: false,
            useBom: true,
            useKeysAsHeaders: false,
            headers: [
                'N° échantillon',
                'Millésime',
                'Raison sociale',
                'CVI',
                'SIRET',
                'Opérateur - Adresse',
                'Opérateur - Code postal',
                'Opérateur -  Ville',
                'Cahier des charges',
                'Produit',
                'Couleur',
                'Type de déclaration',
                'Adresse du site',
                'Cépages',
                'TAV',
                'N° de lot',
                'Logement',
                'Volume (hl)',
                'Contenants',
                'Date évènement',
                'Type prélèvement',
                'N° d’expertise',
                'N° de passage',
                'Date butoir',
                'Référence tournée',
                'Date prélèvement',
                'Responsable tournée',
                'Référence commission',
                'Date commission',
                'Jury',
                'N° anonymat',
                'Résultat dégustation'
            ]
        };

        const csvExporter = new ExportToCsv(options);
        csvExporter.generateCsv(data);
    }

    /**
     * Retourne le nom complet de l'intervenant'
     * @param intervenant - Intervenant
     */
    getResponsableTournee(intervenant: Operateur): string {
        if (intervenant) {
            if (intervenant.nom && intervenant.prenom) {
                return intervenant.nom.concat(' ').concat(intervenant.prenom);
            }
            if (intervenant.nom) {
                return intervenant.nom;
            }
            if (intervenant.prenom) {
                return intervenant.prenom;
            }
            return '';
        }
        return '';
    }
}
